import cx from 'classnames';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { AppRoutes } from '@/utils';

import { useOnClickOutside } from '@/hooks';
import { useCurrentPageName } from '@/hooks/useCurrentPageName';

import { NavbarEntry } from '@/types/base';
import { translateDynamicKey } from '@/types/translation';

import { CaretDownCircle } from '@/components/base/Icons/CaretDownIcon';

import { getFeatureFlagWrapper, routeMatches } from './NavEntry.utils';

import Badge from '@/storybook/components/Badge/Badge';
import ConditionalWrapper from '@/storybook/components/ConditionalWrapper/ConditionalWrapper';
import styles from './NavEntry.module.css';

const NavEntry = ({ entry: { items, basePath, route, label }, testId }: { entry: NavbarEntry; testId: string }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useOnClickOutside(() => setOpen(false), open);
  const currentPageName = useCurrentPageName();

  const currentRoute = window.location.pathname;

  const subRouteActive = useMemo(() => {
    return routeMatches({
      currentRoute,
      basePath,
      navigationItems: items,
    });
  }, [currentRoute, basePath, items]);

  const onClickParentNav = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (items) {
      e.preventDefault();
      setOpen(currentOpen => !currentOpen);
    }
    (document.activeElement as HTMLElement).blur();
  };

  return (
    <>
      <NavLink
        to={!items ? route : ''}
        key={label}
        className={({ isActive, isPending }) =>
          cx(styles.container, {
            [styles.pending]: isPending,
            [styles.active]: (isActive && !items) || subRouteActive || open,
          })
        }
        onClick={onClickParentNav}
        data-testid={testId}
      >
        {label}
        {!!items?.length && (
          <span className={styles.caret}>
            <CaretDownCircle fill={open ? '#022366' : '#21201F'} />
          </span>
        )}
      </NavLink>
      {open && (
        <div ref={ref} className={styles.submenu}>
          {items?.map(subMenuItem => {
            const subMenuItemlabel = subMenuItem.comingSoon ? (
              <div className={styles.comingSoon}>
                {translateDynamicKey(t, subMenuItem.label)}
                <Badge color="blue" variant="light" size="S">
                  {t('components.levelSelectDropdown.comingSoonBadgeLabel')}
                </Badge>
              </div>
            ) : (
              translateDynamicKey(t, subMenuItem.label)
            );

            return (
              <ConditionalWrapper
                key={subMenuItem.label}
                condition={!!subMenuItem.featureFlag}
                wrapper={getFeatureFlagWrapper(subMenuItem.featureFlag)}
              >
                <NavLink
                  data-testid={`${testId}-submenu-${subMenuItem.id}`}
                  to={AppRoutes[subMenuItem.id as keyof typeof AppRoutes]}
                  state={currentPageName ? { from: currentPageName } : undefined}
                  aria-disabled={subMenuItem.disabled || subMenuItem.comingSoon}
                  tabIndex={subMenuItem.comingSoon ? -1 : undefined}
                  onClick={e => {
                    if (subMenuItem.disabled || subMenuItem.comingSoon) {
                      e.preventDefault();
                      return;
                    }
                    setOpen(false);
                    (document.activeElement as HTMLElement).blur();
                  }}
                  className={({ isActive }) => {
                    return cx(styles.container, {
                      [styles.disabled]: subMenuItem.disabled || subMenuItem.comingSoon,
                      [styles.active]: isActive,
                    });
                  }}
                >
                  {subMenuItemlabel}
                </NavLink>
              </ConditionalWrapper>
            );
          })}
        </div>
      )}
    </>
  );
};

export default NavEntry;
